/**
 * Versions en vigueur des documents légaux (lecture publique, sans connexion), chargées une fois
 * par page et partagées entre les composants. Sert le lien vers la politique de confidentialité
 * (connexion, inscription, menu d'aide) — plan feat-acceptation-cgv-dpa, demande du 13/10/2026.
 * Échec ou document absent : `null`, et le lien ne s'affiche pas.
 */
import { useEffect, useState } from "react";
import { legalDocumentsApi } from "../lib/api";

export interface PublicLegalVersion {
  id: string;
  kind: "cgv" | "dpa" | "rh_annex" | "privacy";
  version: string;
  effectiveOn: string;
  pdfSha256: string;
}
export interface CurrentLegalDocuments {
  cgv: PublicLegalVersion | null;
  dpa: PublicLegalVersion | null;
  privacy: PublicLegalVersion | null;
  rhAnnex: PublicLegalVersion | null;
}

let pending: Promise<CurrentLegalDocuments | null> | null = null;

/**
 * Charge (une fois) les versions en vigueur, partagées par toute la page. `force` relit le serveur
 * (documents mis à jour pendant la saisie). Rend `null` en cas d'échec.
 */
export function loadCurrentLegalDocuments(force = false): Promise<CurrentLegalDocuments | null> {
  if (force) pending = null;
  if (!pending) {
    pending = fetch("/api/public/legal-documents/current")
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((d) => {
        if (!d) pending = null; // nouvel essai au prochain affichage
        return d;
      });
  }
  return pending;
}

export function useCurrentLegalDocuments(): CurrentLegalDocuments | null {
  const [docs, setDocs] = useState<CurrentLegalDocuments | null>(null);
  useEffect(() => {
    let alive = true;
    void loadCurrentLegalDocuments().then((d) => { if (alive) setDocs(d); });
    return () => { alive = false; };
  }, []);
  return docs;
}

/** Adresse publique du PDF d'une version (source unique : `legalDocumentsApi.publicPdfUrl`). */
export const publicLegalPdfUrl = legalDocumentsApi.publicPdfUrl;
